/*
 * ws-sample-form.js — Wax Supply sample request, masked form.
 *
 * The custom UI is a skin over the Shopify Forms app embed. This file never POSTs
 * anything. It writes the customer's answers into the app's own inputs inside its
 * shadow root and then clicks the app's own submit button, so the app's normal
 * pipeline runs untouched: hCaptcha, the customer record, the shopify-forms-333135
 * tag, and the segment that gates TryWaxSup25 eligibility. A custom POST would look
 * identical to the customer and break that whole chain silently.
 *
 * The mask boots hidden and the real form boots visible. Takeover only happens once
 * every required selector resolves. If the app ships a DOM change, the customer gets
 * the original working form rather than a broken custom one.
 */
(function () {
  'use strict';

  var EMBED = 'shopify-forms-embed';

  /* Map: our field -> selector inside the app's shadow root.
   *
   * The split here is not stylistic. The five text inputs carry an `id` and no
   * `name`; the five textareas carry a `name` and no `id`. Only phone has both.
   * Selecting the wrong way returns null for half the form.
   *
   * `required: true` means the mask stands down if it is missing, because without
   * it the submission would be invalid or unattributable. Optional fields are
   * written when present and skipped when not — losing a "how soon are you
   * ordering" answer is not worth losing the whole feature over. */
  var FIELDS = {
    first_name: { sel: '[id="first_name"]', required: true },
    last_name:  { sel: '[id="last_name"]', required: true },
    email:      { sel: '[id="email"]', required: true },
    phone:      { sel: '[id="phone_number"]', required: true },
    company:    { sel: '[id="custom#company_name"]', required: true },
    website:    { sel: '[id="custom#website_or_social_link"]', required: true },
    /* Typo below ("helps is pair you") is baked into the field handle by the Forms
     * app. Do not "fix" it here — the handle is the contract. Fixing the label in
     * admin is safe; recreating the field changes the handle and breaks this. */
    brand:    { sel: '[name="custom#what_wax_brand_you_are_currently_using_this_helps_is_pair_you_wi"]', required: true },
    reason:   { sel: '[name="custom#reason"]', required: true },
    volume:   { sel: '[name="custom#how_much_wax_do_you_purchase_per_order"]', required: true },
    timeline: { sel: '[name="custom#how_soon_are_you_looking_to_place_your_next_order"]', required: true },
    comments: { sel: '[name="custom#questions_comments"]', required: false }
  };
  var SUBMIT_SEL = '[data-testid="btn-form-submit"]';

  var PROBLEM_TRAITS = {
    breaks:     ['never-breaks', 'hyper-elastic', 'elastic'],
    'fine-hair':['strong-grip', 'ultra-thin', 'max-visibility'],
    messy:      ['fast-set', 'ultra-thin', 'quick-set'],
    slow:       ['fast-set', 'quick-set'],
    irritation: ['sensitive-skin', 'hypoallergenic', 'gentle', 'rosin-free', 'conditioning'],
    painful:    ['gentle', 'flexible-pull', 'sensitive-skin', 'conditioning'],
    scent:      ['unscented'],
    price:      []
  };
  var SERVICE_TRAITS = {
    brazilian: ['sensitive-skin', 'strong-grip', 'conditioning'],
    face:      ['delicate-areas', 'gentle', 'flexible-pull'],
    body:      ['hyper-elastic', 'fast-set', 'elastic'],
    underarms: ['delicate-areas', 'strong-grip'],
    mens:      ['strong-grip', 'hyper-elastic', 'elastic']
  };
  /* Operator rule: a student gets exactly one recommendation. Students are the
   * heaviest redeemers of a 100%-off code and the least likely to convert to a
   * wholesale order, so we nudge toward a single redemption. The cart-limit app
   * stays as the hard backstop — this is the soft nudge in front of it. */
  var STUDENT_TERMS = ['student', 'students', 'esthetics school', 'esthetician school', 'in school', 'cosmetology school', 'beauty school'];

  var POPULAR = { ultrablack: 4, pinkcream: 4 };

  function emit(name, detail) {
    try {
      document.dispatchEvent(new CustomEvent('ws:sampleform', { detail: Object.assign({ event: name }, detail || {}) }));
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(Object.assign({ event: 'ws_sampleform_' + name }, detail || {}));
    } catch (e) { /* telemetry must never break the funnel */ }
  }

  function digits(s) { return String(s == null ? '' : s).replace(/\D/g, ''); }

  /* The phone widget accepts a value typed the way a person types it and then
   * reformats it. Hand it the shape it formatted correctly under test rather than
   * a bare digit run, so it has nothing to guess about the country code. */
  function telValue(raw) {
    var d = digits(raw);
    if (d.length === 11 && d.charAt(0) === '1') d = d.slice(1);
    if (d.length === 10) return '+1 ' + d.slice(0, 3) + ' ' + d.slice(3, 6) + ' ' + d.slice(6);
    return String(raw).charAt(0) === '+' ? String(raw) : '+' + d;
  }

  /* Did the value actually survive? Exact match everywhere except phone, which
   * reformats what it accepts — so compare the one thing it cannot change. */
  function landed(el, value) {
    if (el.value === value) return true;
    if (el.type === 'tel') return digits(el.value).length > 0 && digits(el.value) === digits(value);
    return false;
  }

  function nativeSet(el, value) {
    var proto = el.tagName === 'TEXTAREA' ? window.HTMLTextAreaElement.prototype : window.HTMLInputElement.prototype;
    var desc = Object.getOwnPropertyDescriptor(proto, 'value');
    if (desc && desc.set) { desc.set.call(el, value); } else { el.value = value; }
    el.dispatchEvent(new Event('input', { bubbles: true }));
    el.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /* Two write paths, because the app's inputs are not uniform.
   *
   * Ten of the eleven fields take the native prototype setter plus input/change,
   * proven by reading them back off a live submission. The phone field does NOT: it
   * is a controlled widget that reverts a synthetic write and re-syncs to "+1 " from
   * its own internal state. Verified live 2026-08-09 — still "+1 " at 0/50/150/400/
   * 900/1800ms after the setter ran. Nothing about the page looks wrong when this
   * happens, and phone is required by the app, so the submission dies after the
   * customer has already been told they are done: no customer record, no
   * shopify-forms-333135 tag, no segment, no TryWaxSup25.
   *
   * execCommand('insertText') into a focused, range-selected field is the one path
   * the widget honours — it produces the real beforeinput/input events it listens
   * for, formats to "+1 (561) 555-0142", and holds through blur. Deprecated, but
   * there is no other way to drive a controlled input we do not own.
   *
   * Setter first, insertText as the fallback: the cheap path stays on the ten fields
   * that already work (no focus, no selection, no scroll risk, and setSelectionRange
   * throws on type=email), and only the field that needs the expensive path pays for
   * it. Either way the value is read back, because a write that does not land has to
   * be visible to the caller instead of silently discarded. */
  function setValue(el, value) {
    if (!el) return false;
    value = value == null ? '' : String(value);
    if (el.type === 'tel' && value) value = telValue(value);

    nativeSet(el, value);
    if (!value || landed(el, value)) return true;

    /* preventScroll: the embed is concealed but still in flow, and focusing it
     * without this yanks the page off the screen the customer is reading. */
    try {
      el.focus({ preventScroll: true });
      try { el.setSelectionRange(0, el.value.length); } catch (e) { el.select(); }
      document.execCommand('insertText', false, value);
      el.blur();
    } catch (e) { /* nothing left to try; the read-back below reports it */ }
    return landed(el, value);
  }

  function Bridge(root) {
    this.root = root;
    this.shadow = null;
    this.host = null;
    this.nodes = {};
    this.submitBtn = null;
  }

  Bridge.prototype.resolve = function () {
    var embed = document.querySelector(EMBED);
    if (!embed || !embed.shadowRoot) return false;
    var sr = embed.shadowRoot;
    if (!sr.querySelector('form')) return false;

    var nodes = {};
    for (var key in FIELDS) {
      var node = sr.querySelector(FIELDS[key].sel);
      if (!node && FIELDS[key].required) return false;
      if (node) nodes[key] = node;
    }
    var btn = sr.querySelector(SUBMIT_SEL);
    if (!btn) return false;

    this.shadow = sr;
    this.host = embed.closest('[id^="shopify-block-"]') || embed;
    this.nodes = nodes;
    this.submitBtn = btn;
    return true;
  };

  /* Hidden, not removed. The embed keeps rendering and stays in the accessibility
   * tree's dead zone rather than being display:none'd, because the invisible
   * hCaptcha lives in this subtree and a display:none ancestor is the kind of thing
   * that makes a bot check quietly start failing.
   *
   * NOTE FOR ANYONE DEBUGGING THIS: `host` is the shopify-block WRAPPER, not the
   * <shopify-forms-embed> element (see resolve()). The web component rewrites its
   * OWN style attribute to "display: block" as it connects, so if you inspect the
   * embed element you will see an unconcealed, ~2400px-tall element and conclude the
   * conceal has failed. It has not. That element sits inside this 1px, clipped,
   * zero-opacity wrapper and paints nothing. Measure the wrapper, or elementFromPoint
   * over the region — verified live 2026-08-09. */
  Bridge.prototype.conceal = function () {
    var s = this.host.style;
    /* stage() may have left offsets behind; clear them or the 1px box drifts. */
    s.removeProperty('top');
    s.removeProperty('left');
    s.removeProperty('z-index');
    s.setProperty('position', 'absolute', 'important');
    s.setProperty('width', '1px', 'important');
    s.setProperty('height', '1px', 'important');
    s.setProperty('overflow', 'hidden', 'important');
    s.setProperty('clip-path', 'inset(50%)', 'important');
    s.setProperty('opacity', '0', 'important');
    s.setProperty('pointer-events', 'none', 'important');
    this.host.setAttribute('aria-hidden', 'true');
  };

  /* Every escape hatch routes through here, so this is what hands the customer back a
   * real, usable form. Keep it to inline styles and the attribute — anything keyed in
   * the stylesheet with !important would survive this call and strand them. */
  Bridge.prototype.reveal = function () {
    this.host.removeAttribute('style');
    this.host.removeAttribute('aria-hidden');
  };

  /* THE ONE THING THAT MUST HAPPEN BEFORE EVERY SUBMIT.
   *
   * hCaptcha positions its challenge popup against the widget anchor at the moment
   * it opens. conceal() puts that anchor inside a 1px box with clip-path: inset(50%),
   * roughly 4,600px down its own clipped scroll region — so on 2026-08-10 a challenge
   * for a real customer opened somewhere she could never see or reach. The token never
   * resolved, the app never posted, no entry was created, no error text appeared, and
   * the old fail-open timeout showed her a discount code for a lead that did not exist.
   * Five of ten leads that night died exactly this way, all of them paid clicks.
   *
   * So: give the anchor a real, on-screen rect before clicking submit. The wrapper is
   * still invisible (opacity 0) and still paints nothing, and it is position: fixed so
   * nothing in the page reflows. The challenge popup is a child of <body> at
   * z-index 2000000000 — it inherits neither the opacity nor the pointer-events: none,
   * so it lands on top of the page and takes clicks normally.
   *
   * Do not "simplify" this back to conceal(). Measure it instead: stage, submit, and
   * check that document.querySelector('iframe[src*="frame=challenge"]') resolves to a
   * box inside the viewport. */
  Bridge.prototype.stage = function () {
    var s = this.host.style;
    s.removeProperty('clip-path');
    s.setProperty('position', 'fixed', 'important');
    s.setProperty('top', '0', 'important');
    s.setProperty('left', '0', 'important');
    s.setProperty('width', '100vw', 'important');
    s.setProperty('height', '100vh', 'important');
    s.setProperty('overflow', 'hidden', 'important');
    s.setProperty('opacity', '0', 'important');
    s.setProperty('pointer-events', 'none', 'important');
    s.setProperty('z-index', '0', 'important');
    this.host.setAttribute('aria-hidden', 'true');

    /* Now put the anchor in the middle of the screen.
     *
     * The captcha anchor is a ZERO-HEIGHT div and it is the last element in the form,
     * so scrolling the staged window can only ever bring it to the very bottom edge —
     * measured live at y=1112 in a 1112px viewport, with the scroll already clamped
     * (content 1276px, window 1112px, max scroll 164px). Scrolling harder does nothing.
     *
     * So scroll to the end, measure where the anchor actually landed, and slide the
     * whole staged box up by the difference. The box is invisible and fixed, so moving
     * it costs nothing and reflows nothing; only the anchor's screen position matters,
     * because that is what hCaptcha measures when it decides where to open the popup.
     *
     * Best effort by design — if the anchor cannot be found the challenge may still
     * open off-screen, and awaitResult now fails closed instead of pretending the
     * submission worked. */
    try {
      var anchor = this.shadow.querySelector('iframe[src*="hcaptcha"]');
      anchor = anchor && anchor.parentElement;
      if (anchor) {
        this.host.scrollTop = this.host.scrollHeight;
        var shift = anchor.getBoundingClientRect().top - (window.innerHeight / 2);
        if (shift) s.setProperty('top', Math.round(-shift) + 'px', 'important');
      }
    } catch (e) {}
  };

  /* Is hCaptcha showing a real, interactive challenge right now? Invisible mode parks
   * the challenge frame at top:-10000px with visibility:hidden and only moves it into
   * view when it wants the visitor to prove themselves. A visible one means a human is
   * being asked to do something, which is the one case where waiting longer is right. */
  function captchaOpen() {
    try {
      var frames = document.querySelectorAll('iframe[src*="hcaptcha"][src*="frame=challenge"]');
      for (var i = 0; i < frames.length; i++) {
        var box = frames[i].parentElement && frames[i].parentElement.parentElement;
        if (!box) continue;
        var cs = getComputedStyle(box);
        if (cs.visibility === 'hidden' || cs.display === 'none' || parseFloat(cs.opacity) === 0) continue;
        var r = box.getBoundingClientRect();
        if (r.width > 40 && r.height > 40 && r.bottom > 0 && r.top < window.innerHeight) return true;
      }
    } catch (e) {}
    return false;
  }

  /* Returns the list of REQUIRED fields whose value did not survive the write.
   * Empty means it is safe to click submit. A failed optional field costs one
   * answer; a failed required one costs the whole lead, so only those are
   * reported and only those block. */
  Bridge.prototype.fill = function (data) {
    var n = this.nodes;
    var failed = [];
    function put(key, el, value) {
      if (!el) return;
      if (!setValue(el, value) && FIELDS[key] && FIELDS[key].required) failed.push(key);
    }
    put('first_name', n.first_name, data.first_name);
    put('last_name', n.last_name, data.last_name);
    put('email', n.email, data.email);
    put('phone', n.phone, data.phone);
    put('company', n.company, data.company);
    put('website', n.website, data.website);
    put('brand', n.brand, data.brand);
    put('reason', n.reason, data.reason);
    put('volume', n.volume, data.volume);
    put('timeline', n.timeline, data.timeline);
    put('comments', n.comments, data.comments);
    return failed;
  };

  /* The app shows no success message of any kind — a full DOM sweep including
   * shadow roots found zero confirmation text. Success is therefore only
   * observable as the form tearing itself down. We watch for that, separately for
   * validation text appearing, and separately again for a visible captcha challenge.
   *
   * TIMING OUT INTO SUCCESS WAS THE ORIGINAL SIN AND IT IS GONE. The reasoning was
   * that blocking someone on the screen carrying the discounted cart costs the order,
   * so guessing "it worked" was the cheaper error. That was wrong, and the launch
   * night measured how wrong: a timeout is not an unlucky success, it is the exact
   * signature of a captcha challenge that never resolved. Five leads got a discount
   * code, no customer record, and therefore a code that would be rejected at checkout
   * — the worst of both outcomes. A timeout now FAILS CLOSED: the real form is handed
   * back with every answer already in it, which is one button from done. */
  Bridge.prototype.awaitResult = function (timeoutMs, onChallenge) {
    var sr = this.shadow;
    return new Promise(function (resolve) {
      var done = false;
      var deadline = Date.now() + timeoutMs;
      var challenged = false;

      function finish(result) {
        if (done) return;
        done = true;
        clearInterval(tick);
        obs.disconnect();
        resolve(result);
      }
      var obs = new MutationObserver(function () {
        var form = sr.querySelector('form');
        if (!form || !form.querySelector('input, textarea')) {
          finish({ ok: true, via: challenged ? 'teardown_after_captcha' : 'teardown' });
          return;
        }
        var text = (sr.textContent || '').toLowerCase();
        if (text.indexOf('is required') > -1 || text.indexOf('invalid') > -1 || text.indexOf('try again') > -1) {
          finish({ ok: false, via: 'validation', message: 'The form rejected that. Please check your details and try again.' });
        }
      });
      obs.observe(sr, { childList: true, subtree: true, characterData: true });

      var tick = setInterval(function () {
        /* A visible challenge means a human is mid-task. Stop the clock and say so —
         * counting someone who is actively solving a puzzle as a failure would throw
         * away the very leads this fix exists to keep. */
        if (captchaOpen()) {
          deadline = Date.now() + 120000;
          if (!challenged) { challenged = true; try { onChallenge && onChallenge(); } catch (e) {} }
          return;
        }
        if (Date.now() > deadline) {
          finish({
            ok: false,
            via: challenged ? 'captcha_timeout' : 'timeout',
            message: 'We could not confirm that submission. Your answers are already filled into the form just below — press its submit button and you are done.'
          });
        }
      }, 400);
    });
  };

  Bridge.prototype.submit = function () { this.submitBtn.click(); };

  /* ---------------- recommender ---------------- */

  function isStudent(answers) {
    /* "Anywhere in the submission" is the operator's rule, so this scans every
     * surface the customer can type into — and the reason box is the likeliest of
     * them ("I'm a student at ..."). brandRaw, not brand: the resolver normalises
     * "my school's brand" into a canonical name and the word would be lost. */
    var hay = [answers.brandRaw, answers.brand, answers.company, answers.website,
               answers.reason, answers.notes, answers.volume, answers.timeline]
      .join(' ').toLowerCase();
    for (var i = 0; i < STUDENT_TERMS.length; i++) {
      if (hay.indexOf(STUDENT_TERMS[i]) > -1) return true;
    }
    return false;
  }

  function score(formula, answers, cfg, brandTexture) {
    var s = 0;
    if (answers.texture === formula.texture) {
      s += 45;
    } else if (answers.texture === 'unsure' && brandTexture && brandTexture === formula.texture) {
      /* No stated preference, so we lean on what they're already used to. Weighted
       * well below a stated preference because the brand->texture table is my read
       * of each competitor's flagship, not Wax Supply's own testing. */
      s += 18;
    }

    var traits = formula.traits || [];
    function hits(list) {
      var n = 0;
      for (var i = 0; i < list.length; i++) if (traits.indexOf(list[i]) > -1) n++;
      return n;
    }
    answers.problems.forEach(function (p) { s += hits(PROBLEM_TRAITS[p] || []) * 14; });
    answers.services.forEach(function (v) { s += hits(SERVICE_TRAITS[v] || []) * 9; });

    if (formula.masterQty < cfg.deprioritizeBelow) s -= 25;
    if (formula.masterQty >= 800) s += 5;
    s += POPULAR[formula.key] || 0;
    return s;
  }

  function recommend(formulas, answers, cfg) {
    var brandTexture = answers.brandTexture || '';
    var pool = formulas.filter(function (f) {
      /* Three independent ways a formula is disqualified before it can ever be
       * shown. All three are silent-failure guards, not preferences. */
      if (!f.resolved) return false;              // product/SKU lookup missed
      if (!f.sampleOk) return false;              // permalink would drop the line
      if (f.masterQty < cfg.hideBelow) return false; // can't back the demand
      return true;
    });

    pool.forEach(function (f) { f._score = score(f, answers, cfg, brandTexture); });
    pool.sort(function (a, b) { return b._score - a._score; });

    var limit = answers.student ? 1 : cfg.maxPicks;
    var picks = [];
    if (pool.length) picks.push(pool[0]);

    for (var i = 1; i < pool.length && picks.length < limit; i++) {
      var cand = pool[i];
      /* When they told us nothing about texture, the second pick is deliberately a
       * different texture. One gel and one cream is a real comparison; two gels is
       * a coin flip they have to make blind. When they DID state a preference we
       * respect it and give two of what they asked for. */
      if (answers.texture === 'unsure' && cand.texture === picks[0].texture) continue;
      picks.push(cand);
    }
    /* Preference above is soft — if the diversity rule starved the list, fill it. */
    for (var j = 1; j < pool.length && picks.length < limit; j++) {
      if (picks.indexOf(pool[j]) === -1) picks.push(pool[j]);
    }

    return { picks: picks, rest: pool.filter(function (f) { return picks.indexOf(f) === -1; }) };
  }

  /* The permalink is the whole delivery mechanism: it builds the cart, applies the
   * discount, and — critically — carries the email forward.
   *
   * TryWaxSup25 is not open to everyone. It resolves against two customer segments,
   * and membership is decided by the customer record Shopify created from the address
   * typed on this form. Check out under a different address and there is no error and
   * no warning; the discount simply does not attach and the order arrives at full
   * price. Pre-filling checkout with the same address is what closes that gap, and it
   * is why the email is threaded down here rather than only mentioned in copy. */
  function cartUrl(picks, code, email) {
    var parts = picks.map(function (f) { return f.sampleVariantId + ':1'; });
    var url = '/cart/' + parts.join(',') + '?discount=' + encodeURIComponent(code);
    if (email) url += '&checkout[email]=' + encodeURIComponent(email);
    return url;
  }

  /* The theme publishes a colour for every wax it sells. Reading those rather than
   * inventing an accent is what makes a formula look the same on this screen as it
   * does on its own product page. Anything unmapped falls back to ink. */
  var WAX_SLUG = {
    ultrablack: 'ultra-black', euroclear: 'euro-clear', euroblue: 'euro-blue',
    pinkshimmer: 'pink-shimmer', pinkcream: 'pink-cream', purple: 'purple',
    coco: 'coco', europearl: 'euro-pearl'
  };
  function waxColor(key) {
    var slug = WAX_SLUG[key];
    return slug ? 'var(--ws-wax-' + slug + ', var(--ws-sf-ink))' : 'var(--ws-sf-ink)';
  }

  /* ---------------- controller ---------------- */

  function init(root) {
    var cfg = {
      discount: root.dataset.discount || 'TryWaxSup25',
      hideBelow: parseInt(root.dataset.hideBelow, 10) || 100,
      deprioritizeBelow: parseInt(root.dataset.deprioritizeBelow, 10) || 250,
      maxPicks: parseInt(root.dataset.maxPicks, 10) || 2
    };

    var formulas = [];
    var brands = { brands: [], generic: { patterns: [] } };
    try { formulas = JSON.parse(root.querySelector('[data-ws-sf-formulas]').textContent); } catch (e) {}
    try { brands = JSON.parse(root.querySelector('[data-ws-sf-brands]').textContent) || brands; } catch (e) {}

    var mask = root.querySelector('[data-ws-sf-mask]');
    var errBox = root.querySelector('[data-ws-sf-error]');
    var bridge = new Bridge(root);

    /* -- brand type-ahead -------------------------------------------------
     * Resolving the brand at INPUT time is the actual mechanism for lifting
     * resolution off ~64%. A smarter matcher run after the fact cannot recover
     * a misspelling the customer already committed to; a datalist prevents it. */
    var list = root.querySelector('[data-ws-sf-brandlist]');
    var brandIndex = {};
    (brands.brands || []).forEach(function (b) {
      var opt = document.createElement('option');
      opt.value = b.canonical;
      list.appendChild(opt);
      (b.aliases || []).concat([b.canonical]).forEach(function (a) {
        brandIndex[String(a).toLowerCase()] = b;
      });
    });

    function resolveBrand(raw) {
      var v = String(raw || '').trim().toLowerCase();
      if (!v) return { canonical: '', texture: '', resolved: false };
      var generic = (brands.generic && brands.generic.patterns) || [];
      if (generic.indexOf(v) > -1) return { canonical: 'Unknown', texture: '', resolved: true };
      var hit = brandIndex[v];
      if (hit) return { canonical: hit.canonical, texture: hit.suggestedTexture === 'unknown' ? '' : hit.suggestedTexture, resolved: true };
      return { canonical: raw, texture: '', resolved: false };
    }

    /* -- steps ------------------------------------------------------------ */
    var currentStep = 1;
    function goStep(n) {
      currentStep = n;
      root.querySelectorAll('[data-ws-sf-step]').forEach(function (p) {
        p.classList.toggle('is-active', p.dataset.wsSfStep === String(n));
      });
      root.querySelectorAll('[data-step-dot]').forEach(function (d) {
        d.classList.toggle('is-active', parseInt(d.dataset.stepDot, 10) <= n);
      });
      root.scrollIntoView({ behavior: 'smooth', block: 'start' });
      emit('step', { step: n });
    }

    /* -- required-field enforcement --------------------------------------- *
     *
     * Shopify Form 333135 requires SIX fields beyond name/email/phone: company
     * name, website-or-social, current brand, reason, volume and timeline.
     *
     * The app never says so anywhere a script can read. No `required` attribute,
     * no aria-required, no schema — the fields look identical to the optional
     * ones. It only says so AFTER submit, by painting "Field is required" INSIDE
     * ITS OWN SHADOW ROOT, which this page deliberately keeps concealed.
     *
     * So a customer who skipped any one of the six saw a spinner, then a success
     * screen with a discount code, while the submission was rejected ~29ms in and
     * no Shopify customer record was ever created. No customer record means no
     * `shopify-forms-333135` tag, and that tag is what the discount's customer
     * segment gates on — so the code they had just been handed was guaranteed to
     * be rejected at checkout. Measured at 5 of 10 leads on the first night live.
     *
     * Enforcing the six here is the fix, and it is the honest place for it: the
     * store's published terms already say incomplete or "N/A" submissions get
     * canceled, so these were never really optional. The customer just had no way
     * to find out until the lead was already lost.
     *
     * If any of these is ever made optional in the Forms app, drop it from
     * REQUIRED here AND from FIELDS above. Keeping the two in sync is the whole
     * contract — they are two halves of one assertion about the same form. */
    var REQUIRED = {
      1: [
        ['brand', 'Let us know what wax you use now — "not sure" is a perfectly good answer.'],
        ['reason', 'Tell us why you are looking around. A few words is plenty.'],
        ['volume', 'Choose roughly how much wax you order.'],
        ['timeline', 'Choose when you are ordering next.']
      ],
      2: [
        ['first_name', 'Please add your first name.'],
        ['last_name', 'Please add your last name.'],
        ['email', 'Please add your email address.'],
        ['phone', 'Please add a phone number we can reach you on.'],
        ['company', 'Please add your business name.'],
        ['website', 'Please add your website or Instagram — this is how we verify you are a pro.']
      ]
    };

    function fieldEl(name) { return mask.querySelector('[data-ws-sf-input="' + name + '"]'); }

    /* The --half rows wrap each input in its own div, everything else puts the
     * input directly inside .ws-sf__field. parentElement is the right anchor in
     * both cases, which is why this does not go looking for a class. */
    function errSlot(el) {
      var wrap = el.parentElement;
      var slot = wrap.querySelector('[data-ws-sf-fielderr]');
      if (!slot) {
        slot = document.createElement('small');
        slot.className = 'ws-sf__fielderr';
        slot.setAttribute('data-ws-sf-fielderr', '');
        slot.setAttribute('role', 'alert');
        wrap.appendChild(slot);
      }
      return slot;
    }

    function markField(el, msg) {
      el.classList.add('is-invalid');
      el.setAttribute('aria-invalid', 'true');
      errSlot(el).textContent = msg;
    }

    function clearField(el) {
      el.classList.remove('is-invalid');
      el.removeAttribute('aria-invalid');
      var wrap = el.parentElement;
      var slot = wrap && wrap.querySelector('[data-ws-sf-fielderr]');
      if (slot) slot.textContent = '';
    }

    /* Checks one step, marks every offender at once rather than one at a time —
     * being sent back four times in a row is how you lose someone — then returns
     * the first one so the caller can put the cursor in it. */
    function checkStep(n) {
      var list = REQUIRED[n] || [], firstBad = null;
      for (var i = 0; i < list.length; i++) {
        var el = fieldEl(list[i][0]);
        if (!el) continue;
        var v = String(el.value || '').trim();
        var bad = !v;
        if (!bad && list[i][0] === 'email') bad = v.indexOf('@') < 1 || v.indexOf('.') < 0;
        if (!bad && list[i][0] === 'phone') bad = v.replace(/\D/g, '').length < 7;
        if (bad) { markField(el, list[i][1]); if (!firstBad) firstBad = el; }
        else clearField(el);
      }
      return firstBad;
    }

    /* Clear a field's error the moment it is answered, so the form stops
     * nagging about something the customer has already fixed. */
    Object.keys(REQUIRED).forEach(function (step) {
      REQUIRED[step].forEach(function (pair) {
        var el = fieldEl(pair[0]);
        if (!el) return;
        var evt = el.tagName === 'SELECT' ? 'change' : 'input';
        el.addEventListener(evt, function () { if (String(el.value || '').trim()) clearField(el); });
      });
    });

    root.querySelectorAll('[data-ws-sf-next]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var target = parseInt(btn.dataset.wsSfNext, 10);
        /* Only gate forward moves. Going Back is always allowed — trapping
         * someone on a step they are trying to leave is worse than a blank
         * answer, and they have to come through here again anyway. */
        if (target > currentStep) {
          var bad = checkStep(currentStep);
          if (bad) {
            fail('Just a couple of answers missing — they are marked below.');
            try { bad.focus({ preventScroll: true }); } catch (e) { bad.focus(); }
            bad.scrollIntoView({ behavior: 'smooth', block: 'center' });
            emit('step_blocked', { step: currentStep });
            return;
          }
          errBox.hidden = true;
        }
        goStep(target);
      });
    });

    function read() {
      function val(name) { var el = mask.querySelector('[data-ws-sf-input="' + name + '"]'); return el ? el.value.trim() : ''; }
      function many(name) {
        return Array.prototype.slice.call(mask.querySelectorAll('[data-ws-sf-input="' + name + '"]:checked'))
          .map(function (el) { return el.value; });
      }
      /* The human-readable chip text, lifted straight off the rendered label
       * rather than kept in a lookup table here. A table would be a second copy
       * of strings that live in the section, and the two would drift the first
       * time someone reworded a chip in Liquid — quietly, into the customer
       * record, where nobody would notice. */
      function manyLabels(name) {
        return Array.prototype.slice.call(mask.querySelectorAll('[data-ws-sf-input="' + name + '"]:checked'))
          .map(function (el) {
            var txt = el.closest('label');
            return txt ? txt.textContent.trim().replace(/\s+/g, ' ') : el.value;
          });
      }
      var texEl = mask.querySelector('[data-ws-sf-input="texture"]:checked');
      var brandRaw = val('brand');
      var brand = resolveBrand(brandRaw);
      var a = {
        first_name: val('first_name'), last_name: val('last_name'),
        email: val('email'), phone: val('phone'),
        company: val('company'), website: val('website'),
        reason: val('reason'), notes: val('notes'),
        brandRaw: brandRaw, brand: brand.canonical, brandResolved: brand.resolved, brandTexture: brand.texture,
        texture: texEl ? texEl.value : 'unsure',
        problems: many('problems'), services: many('services'),
        problemLabels: manyLabels('problems'), serviceLabels: manyLabels('services'),
        volume: val('volume'), timeline: val('timeline')
      };
      a.student = isStudent(a);
      return a;
    }

    function fail(msg) {
      errBox.textContent = msg;
      errBox.hidden = false;
      errBox.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    /* Backstop. The step gates above are the real user-facing enforcement; this
     * re-checks BOTH steps at submit time so nothing reaches the app half
     * answered even if a gate is bypassed — autofill firing late, a stale panel,
     * someone driving the DOM directly. Every field the app requires is checked
     * here, because every one of them is a silently-lost lead otherwise. */
    function validate() {
      var bad = checkStep(1);
      if (bad) {
        goStep(1);
        try { bad.focus({ preventScroll: true }); } catch (e) { bad.focus(); }
        bad.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return 'A couple of answers are missing on the first step — they are marked for you.';
      }
      bad = checkStep(2);
      if (bad) {
        try { bad.focus({ preventScroll: true }); } catch (e) { bad.focus(); }
        bad.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return 'Almost there — the fields marked below still need an answer.';
      }
      return null;
    }

    /* ------------------------------------------------------------------ *
     * WRITING BACK TO THE REAL FORM
     *
     * One rule governs both functions below, and it is not stylistic:
     *
     *   THE CUSTOMER'S OWN WORDS GO FIRST, VERBATIM, AND ARE NEVER
     *   OVERWRITTEN, REORDERED, SUMMARISED OR TRUNCATED.
     *
     * Structured selections are appended AFTER a divider, as an addendum.
     * Never in front, never instead.
     *
     * Why it is written this way: the prose fields are where customers report
     * things no checkbox can anticipate — a competitor out of stock, a price
     * increase, a distributor dropping them, a reformulation gone wrong. That is
     * market intelligence, it arrives unprompted, and it is a large part of what
     * the sample program is actually for. A previous revision wrote chip codes
     * into custom#reason and buried the customer's own note at the bottom of a
     * machine block. Both destroyed signal, and both were invisible failures —
     * the form still submitted, so nothing looked broken.
     *
     * If a whoever-comes-next is tempted to "clean up" these fields into
     * structured data: the structure is already emitted below the divider. The
     * prose is the part that cannot be reconstructed.
     * ------------------------------------------------------------------ */

    var DIVIDER = '\n\n— — —\n';

    /* -> custom#reason ("REASON YOU ARE CONSIDERING ANOTHER BRAND?") */
    function packReason(a) {
      var out = a.reason || '';
      if (a.problemLabels.length) {
        out += (out ? DIVIDER : '') + 'Selected issues: ' + a.problemLabels.join('; ');
      }
      return out;
    }

    /* -> custom#questions_comments ("QUESTIONS/COMMENTS OR HOW DID YOU HEAR
     * ABOUT US?"). Also carries the answers that have no field of their own on
     * form 333135 yet, which is what lets this ship without touching the form's
     * configuration — the form ID is hardcoded into both discount segments, so
     * not touching it is worth real money. Adding proper fields later only
     * improves the reporting; it does not change the ordering rule above. */
    function packComments(a, picks) {
      var lines = [];
      if (a.texture) lines.push('Texture preference: ' + a.texture);
      if (a.serviceLabels.length) lines.push('Services: ' + a.serviceLabels.join(', '));
      if (a.brandRaw) lines.push('Brand entered: ' + a.brandRaw + (a.brandResolved ? '' : ' [UNRESOLVED]'));
      if (a.student) lines.push('STUDENT: yes (single recommendation)');
      lines.push('Recommended: ' + picks.map(function (p) { return p.label; }).join(', '));

      var summary = lines.join('\n');
      return a.notes ? a.notes + DIVIDER + summary : summary;
    }

    mask.addEventListener('submit', function (ev) {
      ev.preventDefault();
      errBox.hidden = true;

      var a = read();
      var problem = validate();
      if (problem) { fail(problem); emit('validation_blocked', { reason: problem }); return; }

      var result = recommend(formulas, a, cfg);
      if (!result.picks.length) {
        /* Nothing passed the stock and purchasability gates. Never fake a
         * recommendation to fill the screen — send the lead through and say so. */
        emit('no_stock', {});
      }

      var btn = mask.querySelector('[data-ws-sf-submit]');
      var spinner = mask.querySelector('[data-ws-sf-spinner]');
      btn.disabled = true;
      spinner.hidden = false;

      var failed = bridge.fill({
        first_name: a.first_name, last_name: a.last_name, email: a.email, phone: a.phone,
        company: a.company, website: a.website,
        brand: a.brandRaw, reason: packReason(a),
        volume: a.volume, timeline: a.timeline,
        comments: packComments(a, result.picks)
      });

      /* Never click submit on a form we know is incomplete. The app would reject it
       * for the missing required field, the mask would time out into the success
       * path, and the customer would be shown a discounted cart while their lead
       * quietly did not exist. Hand them the real form instead — every answer is
       * already written into it, so finishing is one button — and make the failure
       * loud in telemetry, because this only breaks when the app ships a change. */
      if (failed.length) {
        btn.disabled = false;
        spinner.hidden = true;
        fail('Something on our end blocked that. Your answers are already filled into the form just below — press its submit button and you are done.');
        bridge.reveal();
        try { bridge.host.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch (e) {}
        emit('write_failed', { fields: failed.join(',') });
        return;
      }

      emit('submit', { student: a.student, picks: result.picks.map(function (p) { return p.key; }), brandResolved: a.brandResolved });

      /* Stage BEFORE clicking, always. See Bridge.prototype.stage — a concealed
       * anchor is why a challenge can open where nobody can reach it. */
      bridge.stage();

      var waiting = bridge.awaitResult(12000, function () {
        /* Challenge is on screen. Swap the dead spinner for an instruction, because
         * a customer who does not know a puzzle is waiting on them just leaves — one
         * did, five seconds in, on launch night. */
        spinner.hidden = true;
        fail('Quick security check — please complete the box on screen and we will finish up automatically.');
        emit('captcha_shown', {});
      });
      bridge.submit();

      waiting.then(function (res) {
        btn.disabled = false;
        spinner.hidden = true;
        if (!res.ok) {
          bridge.reveal();
          try { bridge.host.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch (e) {}
          fail(res.message);
          emit('submit_failed', { via: res.via });
          return;
        }
        bridge.conceal();
        errBox.hidden = true;
        emit('submit_ok', { via: res.via });
        showResult(a, result);
      });
    });

    /* -- result screen ----------------------------------------------------
     * Two forces pull against each other on this screen.
     *
     * The recommendation has to feel DECIDED. That is the entire bet behind the
     * rebuild: a pick shown inside the hour where most conversion happens beats a
     * hand-written email a day later, and it only beats it if it reads as an answer
     * rather than as a menu.
     *
     * But a recommendation the customer cannot overrule is a guess wearing a costume.
     * The people who arrive already knowing they want the black are the people most
     * likely to order, and making them accept our pick — or worse, go hunt the
     * collection and lose the discount — is the expensive way to be right.
     *
     * So: every formula in the program is selectable, and the recommended ones simply
     * arrive pre-ticked. Nobody has to choose. Anybody can. The cart link rebuilds
     * from whatever is ticked at the moment they tap it, never from the original
     * recommendation, so the two can never drift apart. */
    function showResult(a, result) {
      var formScreen = root.querySelector('[data-ws-sf-screen="form"]');
      var resScreen = root.querySelector('[data-ws-sf-screen="result"]');
      var picksWrap = root.querySelector('[data-ws-sf-picks]');
      var sub = root.querySelector('[data-ws-sf-result-sub]');
      var cta = root.querySelector('[data-ws-sf-cart]');
      var note = root.querySelector('[data-ws-sf-cta-note]');
      var tally = root.querySelector('[data-ws-sf-tally]');
      var tallyText = root.querySelector('[data-ws-sf-tally-text]');
      var tallyCount = root.querySelector('[data-ws-sf-tally-count]');

      /* Student rule: one recommendation, and the picker is capped at one too.
       * The cart-limit app is the backstop, but a limit the customer hits at the
       * cart is a limit they experience as a rejection. Cap it here instead. */
      var limit = a.student ? 1 : cfg.maxPicks;
      var cards = [];
      var selection = result.picks.slice();

      /* Standing instruction at the cap, plus a one-off confirmation of the last
       * swap. Built here rather than in the Liquid so the section markup does not
       * carry an element that only ever matters once a selection exists. It sits
       * directly under the picks — NOT in the sticky CTA bar, which is already
       * carrying the tally and the button on mobile and has no room to grow. */
      var swapNote = '';
      var swapHint = document.createElement('p');
      swapHint.className = 'ws-sf__swaphint';
      swapHint.setAttribute('role', 'status');
      swapHint.hidden = true;
      if (picksWrap && picksWrap.parentNode) {
        picksWrap.parentNode.insertBefore(swapHint, picksWrap.nextSibling);
      }

      function keysOf(list) { return list.map(function (p) { return p.key; }); }

      function card(f) {
        var el = document.createElement('label');
        el.className = 'ws-sf__card';
        el.style.setProperty('--ws-sf-wax', waxColor(f.key));
        var img = f.image
          ? '<img class="ws-sf__card-img" src="' + f.image + '" alt="" loading="lazy" width="144" height="144">'
          : '';
        el.innerHTML =
          '<input type="checkbox" value="' + f.key + '">' +
          img +
          '<div class="ws-sf__card-body">' +
            '<h3 class="ws-sf__card-title">' + f.label + '</h3>' +
            '<p class="ws-sf__card-blurb">' + f.blurb + '</p>' +
            '<p class="ws-sf__card-why">' + f.bestFor + '</p>' +
          '</div>' +
          '<span class="ws-sf__card-tick" aria-hidden="true">&#10003;</span>';

        var box = el.querySelector('input');
        box.checked = selection.indexOf(f) > -1;
        box.addEventListener('change', function () {
          if (box.checked) {
            /* At the cap, tapping an unpicked formula SWAPS rather than refuses.
             * The previous behaviour disabled every unpicked card at the limit,
             * which read as "these are unavailable" — the customer had no way to
             * know the fix was to untick one of ours first. Swapping out the
             * earliest pick makes the change they were already trying to make,
             * it is visible (the displaced card unticks in front of them), and
             * it is one tap to undo. */
            if (selection.length >= limit) {
              var dropped = selection.shift();
              var dc = cardFor(dropped);
              if (dc) dc.box.checked = false;
              swapNote = dropped.label + ' swapped out for ' + f.label + '.';
              emit('pick_swapped', { out: dropped.key, in: f.key });
            }
            selection.push(f);
          } else {
            var i = selection.indexOf(f);
            if (i > -1) selection.splice(i, 1);
            swapNote = '';
          }
          emit('picks_changed', { picks: keysOf(selection), recommended: keysOf(result.picks) });
          sync();
        });
        cards.push({ el: el, box: box, f: f });
        return el;
      }

      function cardFor(f) {
        for (var i = 0; i < cards.length; i++) { if (cards[i].f === f) return cards[i]; }
        return null;
      }

      function sync() {
        var n = selection.length;
        var atLimit = n >= limit;

        /* At the cap, everything unpicked dims — that is what tells the customer a
         * cap exists. It stays TAPPABLE though (no `disabled`), because a dimmed
         * card that also refuses the tap is a dead end. Dim = "this one costs you
         * a swap", not "this one is unavailable". */
        cards.forEach(function (c) {
          var locked = atLimit && !c.box.checked;
          c.box.disabled = false;
          c.el.classList.toggle('is-locked', locked);
        });

        if (swapHint) {
          if (swapNote) {
            swapHint.textContent = swapNote;
            swapHint.hidden = false;
          } else if (atLimit && cards.length > n) {
            swapHint.textContent = 'That’s your ' + limit + '. Changed your mind? Tap any other formula and it swaps in — no need to untick first.';
            swapHint.hidden = false;
          } else {
            swapHint.hidden = true;
          }
        }

        tallyText.textContent = n
          ? selection.map(function (p) { return p.label; }).join('  +  ')
          : 'Nothing selected';
        tallyCount.textContent = n + ' of ' + limit;

        if (!n) {
          cta.setAttribute('href', '#');
          cta.setAttribute('aria-disabled', 'true');
          cta.textContent = 'Pick at least one';
        } else {
          cta.removeAttribute('aria-disabled');
          cta.setAttribute('href', cartUrl(selection, cfg.discount, a.email));
          cta.textContent = n > 1 ? 'Send me both samples' : 'Send me this sample';
        }
      }

      picksWrap.innerHTML = '';
      result.picks.forEach(function (f) { picksWrap.appendChild(card(f)); });

      if (!result.picks.length) {
        /* Nothing cleared the stock floor. Say so honestly and let the submission,
         * which has already gone through, be the follow-up path. */
        sub.textContent = 'We have your request — our team will follow up with what is in stock for you.';
        root.querySelector('[data-ws-sf-cta]').hidden = true;
      } else {
        sub.textContent = a.student
          ? 'Based on your answers, this is the one to start with. Swap it for any other formula below if you would rather.'
          : 'Based on your answers, ' + (result.picks.length > 1 ? 'these two are' : 'this one is') +
            ' the closest fit. Change either one if you already know what you want.';
      }

      /* Alternates stay collapsed by default. The point of this screen is to make one
       * choice feel obvious inside the hour that most conversions happen in, and a
       * wall of six options rebuilds the exact paralysis the recommender removes. But
       * they are one tap away and fully selectable, so knowing what you want is never
       * punished. */
      if (result.rest.length) {
        var swap = root.querySelector('[data-ws-sf-swap]');
        var alts = root.querySelector('[data-ws-sf-alts]');
        var grid = root.querySelector('[data-ws-sf-alts-grid]');
        result.rest.forEach(function (f) { grid.appendChild(card(f)); });
        swap.hidden = false;
        swap.addEventListener('click', function () {
          alts.hidden = !alts.hidden;
          swap.textContent = alts.hidden ? 'Show me the other formulas' : 'Hide the other formulas';
          emit('alts_toggled', { open: !alts.hidden });
        });
      }

      /* -- the code, and the rule that makes it work --------------------
       * The brief argued for taking the code out of the customer's hands entirely,
       * and the permalink still applies it for them. Showing it anyway is deliberate:
       * a customer who cannot see a code does not believe there is one, and the code
       * is the only way back in for anyone who closes the tab and returns later.
       *
       * The email line underneath is not a nicety. Eligibility resolves against the
       * customer record built from the address on this form, so a different address at
       * checkout means no discount, no error message, and a full-price order. */
      var codeBox = root.querySelector('[data-ws-sf-code]');
      if (codeBox && result.picks.length) {
        root.querySelector('[data-ws-sf-code-val]').textContent = cfg.discount;
        root.querySelector('[data-ws-sf-code-email]').textContent = a.email;
        codeBox.hidden = false;
        var copyBtn = root.querySelector('[data-ws-sf-code-copy]');
        copyBtn.addEventListener('click', function () {
          try { navigator.clipboard.writeText(cfg.discount); } catch (e) {}
          copyBtn.textContent = 'Copied';
          setTimeout(function () { copyBtn.textContent = 'Copy'; }, 2000);
          emit('code_copied', { code: cfg.discount });
        });
      }

      if (result.picks.length) {
        tally.hidden = false;
        note.textContent = 'Applied automatically at checkout. Nothing to type.';
        sync();
      }

      formScreen.hidden = true;
      resScreen.hidden = false;
      root.dataset.state = 'result';
      root.scrollIntoView({ behavior: 'smooth', block: 'start' });
      emit('result_shown', { picks: keysOf(result.picks), student: a.student });

      cta.addEventListener('click', function (e) {
        if (cta.getAttribute('aria-disabled') === 'true') { e.preventDefault(); return; }
        emit('cart_clicked', {
          picks: keysOf(selection),
          recommended: keysOf(result.picks),
          changed: keysOf(selection).join() !== keysOf(result.picks).join()
        });
      });
    }

    /* -- boot -------------------------------------------------------------
     * The app embed loads async, so poll briefly rather than assume it is there.
     * If it never resolves we simply never take over, and the customer uses the
     * original form. That is the whole fail-safe. */
    var tries = 0;
    (function attempt() {
      if (bridge.resolve()) {
        bridge.conceal();
        root.dataset.state = 'ready';
        emit('ready', {});
        return;
      }
      if (++tries > 40) {
        root.dataset.state = 'standdown';
        emit('standdown', { reason: 'forms app selectors did not resolve' });
        return;
      }
      setTimeout(attempt, 250);
    })();
  }

  function boot() {
    document.querySelectorAll('[data-ws-sample-form]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', boot);
})();
